import { supabase } from "@/integrations/supabase/client";
import { 
  Community, 
  CommunityWithDetails, 
  CommunityFilters, 
  CommunityListResponse,
  CreateCommunityData,
  UpdateCommunityData,
  UserCommunityStatus,
  CommunityPermissions,
  CommunityStats 
} from "@/types/community";

// Tipo temporário para a query simplificada
interface CommunitySimplified extends Omit<Community, 'game_id'> {
  games?: {
    id: string;
    name: string;
  };
}

interface CommunityListResponseSimplified {
  communities: CommunitySimplified[];
  total_count: number;
  has_more: boolean;
}

// ===============================
// LISTAGEM DE COMUNIDADES
// ===============================

export const fetchCommunities = async (filters: CommunityFilters = {}): Promise<CommunityListResponseSimplified> => {
  try {
    console.log('🔍 [fetchCommunities] Iniciando busca com filtros:', filters);
    
    const {
      game_id,
      mentor_id,
      search,
      is_active = true,
      limit = 12,
      offset = 0
    } = filters;

    let query = supabase
      .from('communities')
      .select(`
        *,
        games!communities_game_id_fkey (
          id,
          name
        )
      `)
      .eq('is_active', is_active)
      .order('created_at', { ascending: false });

    console.log('🔍 [fetchCommunities] Query base criada');

    // Filtros opcionais
    if (game_id) {
      query = query.eq('game_id', game_id);
      console.log('🔍 [fetchCommunities] Filtro por jogo aplicado:', game_id);
    }

    if (mentor_id) {
      query = query.eq('mentor_id', mentor_id);
      console.log('🔍 [fetchCommunities] Filtro por mentor aplicado:', mentor_id);
    }

    if (search) {
      query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      console.log('🔍 [fetchCommunities] Filtro de busca aplicado:', search);
    }

    // Paginação
    const countQuery = supabase
      .from('communities')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', is_active);

    if (game_id) countQuery.eq('game_id', game_id);
    if (mentor_id) countQuery.eq('mentor_id', mentor_id);
    if (search) countQuery.or(`name.ilike.%${search}%,description.ilike.%${search}%`);

    console.log('🔍 [fetchCommunities] Executando queries...');

    const [{ data: communities, error }, { count, error: countError }] = await Promise.all([
      query.range(offset, offset + limit - 1),
      countQuery
    ]);

    console.log('🔍 [fetchCommunities] Queries executadas. Communities:', communities?.length, 'Count:', count);

    if (error) {
      console.error('❌ [fetchCommunities] Detalhes do erro na query:', error);
      console.error('❌ [fetchCommunities] Código:', error.code);
      console.error('❌ [fetchCommunities] Mensagem:', error.message);
      console.error('❌ [fetchCommunities] Detalhes:', error.details);
      throw error;
    }
    if (countError) {
      console.error('❌ [fetchCommunities] Detalhes do erro no count:', countError);
      throw countError;
    }

    const total_count = count || 0;
    const has_more = offset + limit < total_count;

    console.log('✅ [fetchCommunities] Sucesso! Total:', total_count, 'Has more:', has_more);

    // 🎯 Buscar dados dos mentores/owners das comunidades
    let communitiesWithMentors = communities || [];
    
    if (communities && communities.length > 0) {
      console.log('👥 [fetchCommunities] Buscando dados dos mentores...');
      
      // Extrair IDs únicos dos mentores/owners
      const mentorIds = [...new Set(
        communities
          .map(c => c.owner_id || c.mentor_id)
          .filter(Boolean)
      )];
      
      console.log('🔍 [fetchCommunities] IDs dos mentores encontrados:', mentorIds.length);
      
      if (mentorIds.length > 0) {
        // Buscar dados dos profiles dos mentores
        const { data: mentorProfiles, error: mentorError } = await supabase
          .from('profiles')
          .select('id, nickname, image_url')
          .in('id', mentorIds);
        
        if (mentorError) {
          console.warn('⚠️ [fetchCommunities] Erro ao buscar profiles dos mentores:', mentorError);
        } else {
          console.log('✅ [fetchCommunities] Profiles dos mentores encontrados:', mentorProfiles?.length || 0);
          
          // Buscar imagens dos mentores na tabela mentor_info como fallback
          const { data: mentorImages, error: mentorImagesError } = await supabase
            .from('mentor_info')
            .select('profile_id, image_url')
            .in('profile_id', mentorIds);
          
          if (mentorImagesError) {
            console.warn('⚠️ [fetchCommunities] Erro ao buscar imagens dos mentores:', mentorImagesError);
          }
          
          // Combinar dados dos mentores (priorizando imagem da tabela mentor_info)
          const mentorsData = mentorProfiles?.map(profile => {
            const mentorImage = mentorImages?.find(img => img.profile_id === profile.id);
            const finalImageUrl = mentorImage?.image_url || profile.image_url;
            
            console.log(`📸 [fetchCommunities] Mentor ${profile.nickname}:`, {
              mentor_info_image: mentorImage?.image_url,
              profile_image: profile.image_url,
              final_image: finalImageUrl
            });
            
            return {
              id: profile.id,
              nickname: profile.nickname,
              image_url: finalImageUrl // Prioriza mentor_info.image_url
            };
          }) || [];
          
          // Mapear comunidades com dados dos mentores
          communitiesWithMentors = communities.map(community => {
            const mentorId = community.owner_id || community.mentor_id;
            const mentor = mentorsData.find(m => m.id === mentorId);
            
            return {
              ...community,
              mentor: mentor ? {
                profile: {
                  nickname: mentor.nickname || 'Mentor',
                  image_url: mentor.image_url
                }
              } : {
                profile: {
                  nickname: 'Mentor',
                  image_url: null
                }
              }
            };
          });
          
          console.log('✅ [fetchCommunities] Comunidades com dados dos mentores:', communitiesWithMentors.length);
        }
      }
    }

    // Mapear cover_image para cover_image_url para compatibilidade
    const mappedCommunities = communitiesWithMentors.map(community => ({
      ...community,
      cover_image_url: community.cover_image_url || community.cover_image
    })) as CommunitySimplified[];

    return {
      communities: mappedCommunities,
      total_count,
      has_more
    };

  } catch (error) {
    console.error('❌ [fetchCommunities] Erro ao buscar comunidades:', error);
    throw new Error('Falha ao carregar comunidades');
  }
};

// ===============================
// BUSCAR COMUNIDADE INDIVIDUAL
// ===============================

export const fetchCommunityBySlug = async (slug: string): Promise<CommunityWithDetails | null> => {
  console.log('🔍 [fetchCommunityBySlug] Buscando comunidade com slug:', slug);
  
  try {
    // Query básica da comunidade
    const { data: communityData, error: communityError } = await supabase
      .from('communities')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .single();

    if (communityError || !communityData) {
      console.error('❌ [fetchCommunityBySlug] Comunidade não encontrada:', communityError);
      return null;
    }

    console.log('✅ [fetchCommunityBySlug] Dados básicos encontrados:', communityData);

    // Buscar dados do jogo - usando apenas campos básicos
    console.log('🎮 [fetchCommunityBySlug] Buscando dados do jogo:', communityData.game_id);
    
    let gameData = null;
    try {
      const { data: game, error: gameError } = await supabase
        .from('games')
        .select('id, name, cover_url')
        .eq('id', communityData.game_id)
        .single();
      
      if (!gameError && game) {
        gameData = game;
      } else {
        console.warn('⚠️ [fetchCommunityBySlug] Erro ao buscar jogo (tentando sem cover_url):', gameError?.message);
        
        // Tentar sem cover_url
        const { data: gameBasic, error: gameBasicError } = await supabase
          .from('games')
          .select('id, name')
          .eq('id', communityData.game_id)
          .single();
        
        if (!gameBasicError && gameBasic) {
          gameData = gameBasic;
        }
      }
    } catch (error) {
      console.warn('⚠️ [fetchCommunityBySlug] Jogo não encontrado ou erro:', error);
    }

    // Buscar dados do mentor - usando apenas campos que existem
    console.log('👤 [fetchCommunityBySlug] Buscando dados do mentor:', communityData.mentor_id);
    
    let mentorData = null;
    try {
      // Buscar apenas campos que realmente existem na tabela profiles
      const { data: mentor, error: mentorError } = await supabase
        .from('profiles')
        .select(`
          id, 
          nickname, 
          slug
        `)
        .eq('id', communityData.mentor_id)
        .single();
      
      if (!mentorError && mentor) {
        console.log('✅ [fetchCommunityBySlug] Mentor encontrado com dados básicos:', mentor);
        console.log('🔗 [fetchCommunityBySlug] Slug do mentor:', mentor.slug);
        
        // Buscar imagem do mentor com múltiplos fallbacks
        console.log('🖼️ [fetchCommunityBySlug] Buscando imagem do mentor...');
        let mentorImageUrl = null;
        
        try {
          // 1. Tentar buscar na tabela mentor_info primeiro
          const { data: mentorInfoData, error: mentorInfoError } = await supabase
            .from('mentor_info')
            .select('image_url')
            .eq('profile_id', mentor.id)
            .maybeSingle();
          
          if (!mentorInfoError && mentorInfoData?.image_url) {
            if (mentorInfoData.image_url.startsWith('http')) {
              mentorImageUrl = mentorInfoData.image_url;
            } else {
              // Se é apenas o nome do arquivo, formar caminho completo
              let fullPath = mentorInfoData.image_url;
              if (!mentorInfoData.image_url.includes('/')) {
                fullPath = `${mentor.id}/${mentorInfoData.image_url}`;
              }
              
              const { data: urlData } = supabase.storage
                .from('profile-images')
                .getPublicUrl(fullPath);
              mentorImageUrl = urlData.publicUrl;
            }
            console.log('📸 [fetchCommunityBySlug] Imagem encontrada em mentor_info:', mentorImageUrl);
          }
          
          // 2. Se não encontrou, tentar buscar no campo image_url da tabela profiles
          if (!mentorImageUrl) {
            const { data: profileImageData, error: profileImageError } = await supabase
              .from('profiles')
              .select('image_url')
              .eq('id', mentor.id)
              .single();
            
            if (!profileImageError && profileImageData?.image_url) {
              mentorImageUrl = profileImageData.image_url;
              console.log('📸 [fetchCommunityBySlug] Imagem encontrada na tabela profiles:', mentorImageUrl);
            }
          }
          
          // 3. Se ainda não encontrou, tentar buscar no storage
          if (!mentorImageUrl) {
            const { data: imageFiles, error: storageError } = await supabase
              .storage
              .from('profile-images')
              .list(mentor.id);
            
            if (!storageError && imageFiles && imageFiles.length > 0) {
              // Buscar o arquivo mais recente que comece com 'profile-'
              const profileImages = imageFiles
                .filter(file => file.name.startsWith('profile-'))
                .sort((a, b) => new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime());
              
              if (profileImages.length > 0) {
                mentorImageUrl = supabase.storage
                  .from('profile-images')
                  .getPublicUrl(`${mentor.id}/${profileImages[0].name}`)
                  .data.publicUrl;
                
                console.log('📸 [fetchCommunityBySlug] Imagem mais recente encontrada no storage:', mentorImageUrl);
              }
            } else {
              console.log('📸 [fetchCommunityBySlug] Nenhuma imagem encontrada no storage para o mentor');
            }
          }
        } catch (imageError) {
          console.warn('⚠️ [fetchCommunityBySlug] Erro ao buscar imagem do mentor:', imageError);
        }
        
        mentorData = {
          ...mentor,
          avatar_url: mentorImageUrl // Adicionar a URL da imagem encontrada
        };
        
        console.log('✅ [fetchCommunityBySlug] Mentor completo com imagem:', mentorData);
      } else {
        console.warn('⚠️ [fetchCommunityBySlug] Erro ao buscar mentor:', mentorError?.message);
        
        // Fallback: dados mínimos
        mentorData = {
          id: communityData.mentor_id,
          nickname: 'Mentor',
          slug: null,
          avatar_url: null
        };
      }
    } catch (error) {
      console.warn('⚠️ [fetchCommunityBySlug] Erro na busca do mentor:', error);
      mentorData = {
        id: communityData.mentor_id,
        nickname: 'Mentor',
        slug: null,
        avatar_url: null
      };
    }

    // Montar o objeto final
    const result: CommunityWithDetails = {
      ...communityData,
      cover_image_url: communityData.cover_image_url || communityData.cover_image,
      game: gameData || {
        id: communityData.game_id,
        name: 'Jogo Desconhecido'
      },
      mentor: mentorData || {
        id: communityData.mentor_id,
        nickname: 'Mentor'
      }
    };

    console.log('🎉 [fetchCommunityBySlug] Comunidade completa:', result);
    return result;

  } catch (error: any) {
    console.error('❌ [fetchCommunityBySlug] Erro geral:', error);
    return null;
  }
};

// ===============================
// STATUS DO USUÁRIO NA COMUNIDADE
// ===============================

export const fetchUserCommunityStatus = async (
  communityId: string, 
  userId?: string
): Promise<UserCommunityStatus> => {
  try {
    // Permissões padrão para usuários não autenticados
    const defaultPermissions: CommunityPermissions = {
      can_post: false,
      can_comment: false,
      can_like: false,
      can_edit_posts: false,
      can_delete_posts: false,
      can_pin_posts: false,
      can_moderate: false,
      can_manage_members: false,
      can_edit_community: false
    };

    if (!userId) {
      return {
        is_member: false,
        is_owner: false,
        permissions: defaultPermissions
      };
    }

    // Buscar informações da comunidade
    const { data: community, error: communityError } = await supabase
      .from('communities')
      .select('owner_id, mentor_id')
      .eq('id', communityId)
      .single();

    if (communityError) {
      console.error('Erro ao buscar comunidade:', communityError);
      return {
        is_member: false,
        is_owner: false,
        permissions: defaultPermissions
      };
    }

    // Verificar se é o proprietário/mentor da comunidade
    const is_owner = community.owner_id === userId || community.mentor_id === userId;
    
    // CORREÇÃO: Usuários NÃO são automaticamente membros
    // Eles precisam entrar manualmente na comunidade
    let is_member = false;
    
    // Verificar se o usuário é realmente membro da comunidade
    try {
      console.log('🔍 [fetchUserCommunityStatus] Verificando membership...');
      const { data: membership, error: membershipError } = await supabase
        .from('community_memberships')
        .select('status')
        .eq('community_id', communityId)
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle(); // Use maybeSingle para evitar erro 406
      
      if (membershipError) {
        console.warn('⚠️ [fetchUserCommunityStatus] Erro ao buscar membership:', membershipError);
        is_member = false;
      } else {
        is_member = !!membership;
        console.log('✅ [fetchUserCommunityStatus] Membership encontrado:', !!membership);
      }
    } catch (membershipError) {
      // Se não encontrou membership ou houve erro, não é membro
      console.warn('⚠️ [fetchUserCommunityStatus] Erro na verificação de membership:', membershipError);
      is_member = false;
    }
    
    // APENAS o proprietário é automaticamente considerado membro
    if (is_owner) {
      is_member = true;
      console.log('✅ [fetchUserCommunityStatus] Usuário é owner da comunidade');
    }
    
    // Definir permissões baseadas no status
    const permissions: CommunityPermissions = {
      can_post: is_owner, // Apenas owner pode postar
      can_comment: is_member, // Apenas membros podem comentar
      can_like: is_member, // Apenas membros podem curtir
      can_edit_posts: is_owner, // Apenas owner pode editar posts
      can_delete_posts: is_owner, // Apenas owner pode deletar posts
      can_pin_posts: is_owner, // Apenas owner pode fixar posts
      can_moderate: is_owner, // Apenas owner pode moderar
      can_manage_members: is_owner, // Apenas owner pode gerenciar membros
      can_edit_community: is_owner // Apenas owner pode editar comunidade
    };

    return {
      is_member,
      is_owner,
      permissions
    };

  } catch (error) {
    console.error('Erro ao buscar status do usuário na comunidade:', error);
    return {
      is_member: false,
      is_owner: false,
      permissions: defaultPermissions
    };
  }
};

// ===============================
// ESTATÍSTICAS DA COMUNIDADE
// ===============================

export const fetchCommunityStats = async (communityId: string): Promise<CommunityStats> => {
  try {
    const [
      { count: post_count },
      { data: likes_data },
      { data: comments_data }
    ] = await Promise.all([
      supabase
        .from('community_posts')
        .select('*', { count: 'exact', head: true })
        .eq('community_id', communityId),
      supabase
        .from('community_posts')
        .select('likes_count')
        .eq('community_id', communityId),
      supabase
        .from('community_posts')
        .select('comments_count')
        .eq('community_id', communityId)
    ]);

    const total_likes = likes_data?.reduce((sum, post) => sum + (post.likes_count || 0), 0) || 0;
    const total_comments = comments_data?.reduce((sum, post) => sum + (post.comments_count || 0), 0) || 0;

    // Atividade recente (últimos 7 dias)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const { count: recent_activity_count } = await supabase
      .from('community_posts')
      .select('*', { count: 'exact', head: true })
      .eq('community_id', communityId)
      .gte('created_at', sevenDaysAgo.toISOString());

    return {
      member_count: 0, // Temporário até a tabela community_memberships estar funcionando
      post_count: post_count || 0,
      total_likes,
      total_comments,
      recent_activity_count: recent_activity_count || 0
    };

  } catch (error) {
    console.error('Erro ao buscar estatísticas da comunidade:', error);
    throw new Error('Falha ao carregar estatísticas');
  }
};

// ===============================
// ENTRAR NA COMUNIDADE
// ===============================

export const joinCommunity = async (communityId: string, userId: string): Promise<void> => {
  try {
    console.log(`[joinCommunity] Usuário ${userId} entrando na comunidade ${communityId}`);
    
    // Verificar se já é membro
    const { data: existingMembership, error: checkError } = await supabase
      .from('community_memberships')
      .select('id')
      .eq('community_id', communityId)
      .eq('user_id', userId)
      .maybeSingle();
      
    if (checkError) {
      console.error('Erro ao verificar membership existente:', checkError);
    }

    if (existingMembership) {
      console.log(`[joinCommunity] Usuário já é membro da comunidade`);
      return; // Já é membro
    }

    // Criar membership
    const { error } = await supabase
      .from('community_memberships')
      .insert({
        community_id: communityId,
        user_id: userId,
        status: 'active'
      });
    
    if (error) {
      console.error('Erro ao criar membership:', error);
      throw error;
    }

    console.log(`[joinCommunity] Usuário entrou na comunidade com sucesso`);

  } catch (error) {
    console.error('Erro ao entrar na comunidade:', error);
    throw new Error('Falha ao entrar na comunidade');
  }
};

// ===============================
// SAIR DA COMUNIDADE
// ===============================

export const leaveCommunity = async (communityId: string, userId: string): Promise<void> => {
  try {
    console.log(`[leaveCommunity] Usuário ${userId} saindo da comunidade ${communityId}`);
    
    const { error } = await supabase
      .from('community_memberships')
      .delete()
      .eq('community_id', communityId)
      .eq('user_id', userId);
    
    if (error) {
      console.error('Erro ao deletar membership:', error);
      throw error;
    }

    console.log(`[leaveCommunity] Usuário saiu da comunidade com sucesso`);

  } catch (error) {
    console.error('Erro ao sair da comunidade:', error);
    throw new Error('Falha ao sair da comunidade');
  }
};

// ===============================
// CRIAR COMUNIDADE (Para mentores)
// ===============================

export const createCommunity = async (data: CreateCommunityData): Promise<Community> => {
  try {
    const { data: community, error } = await supabase
      .from('communities')
      .insert(data)
      .select()
      .single();

    if (error) throw error;

    return community as Community;

  } catch (error) {
    console.error('Erro ao criar comunidade:', error);
    throw new Error('Falha ao criar comunidade');
  }
};

// ===============================
// ATUALIZAR COMUNIDADE
// ===============================

export const updateCommunity = async (
  communityId: string, 
  data: UpdateCommunityData
): Promise<Community> => {
  try {
    const { data: community, error } = await supabase
      .from('communities')
      .update(data)
      .eq('id', communityId)
      .select()
      .single();

    if (error) throw error;

    return community as Community;

  } catch (error) {
    console.error('Erro ao atualizar comunidade:', error);
    throw new Error('Falha ao atualizar comunidade');
  }
};

// ===============================
// DELETAR COMUNIDADE
// ===============================

export const deleteCommunity = async (communityId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('communities')
      .delete()
      .eq('id', communityId);

    if (error) throw error;

  } catch (error) {
    console.error('Erro ao deletar comunidade:', error);
    throw new Error('Falha ao deletar comunidade');
  }
};

// ===============================
// VALIDAR SLUG DA COMUNIDADE
// ===============================

export const validateCommunitySlug = async (slug: string, excludeId?: string): Promise<{
  isValid: boolean;
  isAvailable: boolean;
  error?: string;
}> => {
  try {
    // Validar formato
    const slugRegex = /^[a-z0-9-]+$/;
    if (!slugRegex.test(slug) || slug.length < 3 || slug.length > 100) {
      return {
        isValid: false,
        isAvailable: false,
        error: 'Slug deve ter 3-100 caracteres e conter apenas letras minúsculas, números e hífens'
      };
    }

    if (slug.startsWith('-') || slug.endsWith('-') || slug.includes('---')) {
      return {
        isValid: false,
        isAvailable: false,
        error: 'Slug não pode começar/terminar com hífen ou ter hífens consecutivos'
      };
    }

    // Verificar disponibilidade
    let query = supabase
      .from('communities')
      .select('id')
      .eq('slug', slug);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query;

    if (error) throw error;

    const isAvailable = !data || data.length === 0;

    return {
      isValid: true,
      isAvailable,
      error: isAvailable ? undefined : 'Esta URL já está em uso'
    };

  } catch (error) {
    console.error('Erro ao validar slug:', error);
    return {
      isValid: false,
      isAvailable: false,
      error: 'Erro ao validar URL'
    };
  }
}; 